import React, { useState, useMemo } from 'react';
import mapData from '../data/norwayMap.json';

// Map old county regions (pre-2024) to new counties (2024+)
const REGION_TO_COUNTIES = {
  NO54: ['Troms', 'Finnmark'],
  NO50: ['Trøndelag'],
  NO30: ['Akershus', 'Buskerud', 'Østfold'],
  NO03: ['Oslo'],
  NO38: ['Vestfold', 'Telemark'],
  NO42: ['Agder'],
  NO11: ['Rogaland'],
  NO46: ['Vestland'],
  NO15: ['Møre og Romsdal'],
  NO34: ['Innlandet'],
  NO18: ['Nordland'],
};

// Reverse mapping: county name → region ID
const COUNTY_TO_REGION = {};
Object.entries(REGION_TO_COUNTIES).forEach(([regionId, counties]) => {
  counties.forEach(county => { COUNTY_TO_REGION[county] = regionId; });
});

// Display labels for regions on the map (positions based on viewBox 0 0 800 1200)
const REGION_LABELS = {
  NO54: { label: 'Troms og Finnmark', x: 520, y: 200 },
  NO18: { label: 'Nordland', x: 390, y: 400 },
  NO50: { label: 'Trøndelag', x: 330, y: 570 },
  NO15: { label: 'Møre og Romsdal', x: 200, y: 660 },
  NO34: { label: 'Innlandet', x: 350, y: 745 },
  NO46: { label: 'Vestland', x: 180, y: 790 },
  NO11: { label: 'Rogaland', x: 170, y: 895 },
  NO42: { label: 'Agder', x: 280, y: 925 },
  NO38: { label: 'Vestfold/Telemark', x: 335, y: 870 },
  NO30: { label: 'Viken', x: 380, y: 825 },
  NO03: { label: 'Oslo', x: 420, y: 800 },
};

export default function NorwayMap({ selectedCounty, onSelectCounty, dealerCounties = [] }) {
  const [hoveredRegion, setHoveredRegion] = useState(null);

  // Which region IDs have dealers?
  const activeRegions = useMemo(() => {
    const set = new Set();
    dealerCounties.forEach(county => {
      const regionId = COUNTY_TO_REGION[county];
      if (regionId) set.add(regionId);
    });
    return set;
  }, [dealerCounties]);

  // Which region is selected based on the county filter?
  const selectedRegion = selectedCounty ? COUNTY_TO_REGION[selectedCounty] : null;

  const handleRegionClick = (regionId) => {
    const counties = REGION_TO_COUNTIES[regionId];
    if (!counties) return;

    // If clicking the already-selected region, deselect
    if (selectedRegion === regionId) {
      onSelectCounty('');
      return;
    }

    // If the region maps to a single county, select it directly
    if (counties.length === 1) {
      onSelectCounty(counties[0]);
    } else {
      // For multi-county regions, select the first one that has dealers, or the first one
      const withDealers = counties.find(c => dealerCounties.includes(c));
      onSelectCounty(withDealers || counties[0]);
    }
  };

  const getFill = (regionId) => {
    if (selectedRegion === regionId) return '#064e3b'; // emerald-900
    if (hoveredRegion === regionId) return '#065f46'; // emerald-800
    if (activeRegions.has(regionId)) return '#059669'; // emerald-600
    return '#d6d3d1'; // stone-300
  };

  return (
    <div className="relative" data-testid="norway-map">
      <svg
        viewBox={mapData.viewBox}
        className="w-full h-auto max-h-[500px]"
        style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }}
      >
        {Object.entries(mapData.regions).map(([regionId, region]) => (
          <path
            key={regionId}
            d={region.d}
            fill={getFill(regionId)}
            stroke="#ffffff"
            strokeWidth="1.5"
            className="cursor-pointer transition-colors duration-200"
            onClick={() => handleRegionClick(regionId)}
            onMouseEnter={() => setHoveredRegion(regionId)}
            onMouseLeave={() => setHoveredRegion(null)}
            data-testid={`map-region-${regionId}`}
          >
            <title>{region.name}</title>
          </path>
        ))}
        {/* Labels */}
        {Object.entries(REGION_LABELS).map(([regionId, { label, x, y }]) => {
          const isActive = activeRegions.has(regionId) || selectedRegion === regionId;
          return (
            <g key={`label-${regionId}`} className="pointer-events-none select-none">
              <text
                x={x}
                y={y}
                textAnchor="middle"
                fill={isActive ? '#fff' : '#44403c'}
                fontSize="12"
                fontWeight={isActive ? '700' : '500'}
                fontFamily="system-ui, sans-serif"
                stroke={isActive ? 'none' : '#fafaf9'}
                strokeWidth={isActive ? 0 : 0.5}
                paintOrder="stroke"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 justify-center text-xs text-stone-500" data-testid="map-legend">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-emerald-600 inline-block"></span>
          Har forhandler
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-stone-300 inline-block"></span>
          Ingen forhandler
        </div>
      </div>
    </div>
  );
}

export { REGION_TO_COUNTIES, COUNTY_TO_REGION };
